import { FormEditorTypes, formEditorTypes } from "@/components/forms/form-editor.config.ts";
import { ElementIndex, FormEditorContext } from "@/components/forms/form-editor-context.ts";
import { apiFormEditSchema, createDefaultElement } from "@/components/forms/form-editor.types.ts";
import { FormEditQuestionModal } from "@/components/forms/widgets/FormEditQuestionModal.tsx";
import { FormEditTitleDescriptionModal } from "@/components/forms/widgets/FormEditTitleDescriptionModal.tsx";
import { FormSaveTemplateModal } from "@/components/forms/widgets/FormSaveTemplateModal.tsx";
import { ScalesSelectModal } from "@/components/forms/widgets/ScalesSelectModal.tsx";
import { ConfirmationModal, useModals } from "@skailer-com/uikit";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiFormEdit,
  ApiFormElementEdit,
  ApiFormPageEdit,
  ApiFormSectionEdit,
} from "@/types/forms/form-edit.types.ts";
import {
  ApiFormViewElement,
  ApiFormViewPage,
  ApiFormViewSection,
} from "@/types/forms/form-view.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface FormEditorProviderProps {
  children: ReactNode;
  type: FormEditorTypes;
  id: number;
  readOnly?: boolean;
}

export const FormEditorProvider = ({
  children,
  type,
  id,
  readOnly = false,
}: FormEditorProviderProps) => {
  const formEditorType = formEditorTypes[type];
  const formQuery = formEditorType.hooks.useGetFormById(id);
  const { data: form } = formQuery;
  const formUpdate = formEditorType.hooks.useUpdateForm();
  const formReset = formEditorType.hooks.useResetForm();
  const formSaveAsTemplate = formEditorType.hooks.useSaveAsTemplate();
  const [selectedElement, setSelectedElement] = useState<ElementIndex | null>(null);
  const { openModal } = useModals();
  const { t } = useTranslation();

  const methods = useForm<ApiFormEdit>({
    resolver: zodResolver(apiFormEditSchema) as Resolver<ApiFormEdit>,
    defaultValues: { pages: [] },
    mode: "onChange",
  });

  const { reset, getValues, setValue, watch } = methods;

  useEffect(() => {
    if (form) {
      const formEdit: ApiFormEdit = {
        pages: form.pages.map((page: ApiFormViewPage) => ({
          id: page.id,
          title: page.title ?? null,
          description: page.description ?? null,
          sections: page.sections.map((section: ApiFormViewSection) => ({
            id: section.id,
            title: section.title ?? null,
            description: section.description ?? null,
            elements: section.elements.map((element) => ({
              id: element.id,
              type: element.type,
              select_settings: element.select_settings,
              open_answer_settings: element.open_answer_settings,
              file_settings: element.file_settings,
              employee_nps_settings: element.employee_nps_settings,
              text_settings: element.text_settings,
              write_to_field_settings: element.write_to_field_settings,
              scale_settings: element.scale_settings
                ? {
                    ...element.scale_settings,
                    form_scale_id: element.scale_settings?.form_scale.id,
                  }
                : undefined,
              goals_settings: element.goals_settings
                ? {
                    ...element.goals_settings,
                    form_scale_id: element.goals_settings?.form_scale.id,
                  }
                : undefined,
            })),
          })),
        })),
      };

      reset(formEdit);
    }
  }, [form, reset]);

  const formValues = watch() as ApiFormEdit;

  const toggleSelectedElement = (pageIndex: number, sectionIndex: number, elementIndex: number) => {
    if (
      selectedElement &&
      selectedElement.pageIndex === pageIndex &&
      selectedElement.sectionIndex === sectionIndex &&
      selectedElement.elementIndex === elementIndex
    ) {
      setSelectedElement(null);
    } else {
      setSelectedElement({
        pageIndex,
        sectionIndex,
        elementIndex,
      });
    }
  };
  const waitForElementToAppear = async (
    pageIndex: number,
    sectionIndex: number,
    elementIndex: number,
    timeout = 5000
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const interval = 50;
      let waited = 0;

      const check = () => {
        const current = getValues();
        const element =
          current.pages?.[pageIndex]?.sections?.[sectionIndex]?.elements?.[elementIndex];
        if (element?.id) {
          resolve();
        } else if (waited > timeout) {
          reject("Timeout waiting for element");
        } else {
          waited += interval;
          setTimeout(check, interval);
        }
      };

      check();
    });
  };

  const handleAddSection = (pageIndex?: number) => {
    const currentForm = getValues() as ApiFormEdit;
    const newSection: ApiFormSectionEdit = {
      title: null,
      description: null,
      elements: [],
    };

    const lastPageIndex = pageIndex ?? currentForm.pages.length - 1;

    const updatedSections = [...currentForm.pages[lastPageIndex].sections, newSection];
    setValue(`pages.${lastPageIndex}.sections`, updatedSections);
    handleSaveForm();
  };

  const handleAddScale = (position?: {
    pageIndex?: number;
    sectionIndex?: number;
    elementIndex?: number;
  }) => {
    openModal(ScalesSelectModal, {
      onSelect: (scale) => {
        const newElement = {
          type: ApiFormElementType.scale,
          scale_settings: {
            form_scale_id: scale.id,
            is_required: true,
          },
        } as ApiFormElementEdit;

        handleAddElement(ApiFormElementType.scale, position, newElement);
      },
    });
  };

  const handleAddGoalScale = (position?: {
    pageIndex?: number;
    sectionIndex?: number;
    elementIndex?: number;
  }) => {
    openModal(ScalesSelectModal, {
      onSelect: (scale) => {
        const newElement = {
          type: ApiFormElementType.goals,
          goals_settings: {
            form_scale_id: scale.id,
            is_required: true,
            is_single_rating: false,
            show_when_no_goals: false,
          },
        } as ApiFormElementEdit;

        handleAddElement(ApiFormElementType.goals, position, newElement);
      },
    });
  };

  const handleAddElement = async (
    type: ApiFormElementType,
    position?: { pageIndex?: number; sectionIndex?: number; elementIndex?: number },
    element?: ApiFormElementEdit
  ) => {
    const newElement: ApiFormElementEdit = element || createDefaultElement(type);
    const currentForm = getValues() as ApiFormEdit;

    let finalPageIndex: number;
    let finalSectionIndex: number;
    let finalElementIndex: number;

    if (!currentForm.pages.length) {
      setValue("pages", [
        {
          title: null,
          description: null,
          sections: [
            {
              title: null,
              description: null,
              elements: [newElement],
            },
          ],
        },
      ]);
      await handleSaveForm();
      await waitForElementToAppear(0, 0, 0);
      openEditElement(0, 0, 0);
      return;
    }

    if (!position) {
      const lastPageIndex = currentForm.pages.length - 1;
      const lastPage = currentForm.pages[lastPageIndex];

      if (!lastPage.sections.length) {
        const updatedSections: ApiFormSectionEdit[] = [
          ...lastPage.sections,
          {
            title: null,
            description: null,
            elements: [newElement],
          },
        ];

        setValue(`pages.${lastPageIndex}.sections`, updatedSections);
        finalPageIndex = lastPageIndex;
        finalSectionIndex = 0;
        finalElementIndex = 0;
      } else {
        const lastSectionIndex = lastPage.sections.length - 1;
        const lastSection = lastPage.sections[lastSectionIndex];

        const updatedElements = [...lastSection.elements, newElement];
        setValue(`pages.${lastPageIndex}.sections.${lastSectionIndex}.elements`, updatedElements);
        finalPageIndex = lastPageIndex;
        finalSectionIndex = lastSectionIndex;
        finalElementIndex = updatedElements.length - 1;
      }
      await handleSaveForm();
      await waitForElementToAppear(finalPageIndex, finalSectionIndex, finalElementIndex);
      openEditElement(finalPageIndex, finalSectionIndex, finalElementIndex);
      return;
    }

    const { pageIndex = 0, sectionIndex, elementIndex } = position;

    if (pageIndex >= currentForm.pages.length) {
      console.error("Page index out of bounds");
      return;
    }

    const page = currentForm.pages[pageIndex];

    if (sectionIndex === undefined) {
      if (page.sections.length > 0) {
        const lastSectionIndex = page.sections.length - 1;
        const lastSection = page.sections[lastSectionIndex];
        const updatedElements = [...lastSection.elements, newElement];
        setValue(`pages.${pageIndex}.sections.${lastSectionIndex}.elements`, updatedElements);
        finalPageIndex = pageIndex;
        finalSectionIndex = lastSectionIndex;
        finalElementIndex = updatedElements.length - 1;
      } else {
        const newSection = {
          title: null,
          description: null,
          elements: [newElement],
        };
        const updatedSections = [newSection];
        setValue(`pages.${pageIndex}.sections`, updatedSections);
        finalPageIndex = pageIndex;
        finalSectionIndex = 0;
        finalElementIndex = 0;
      }
      await handleSaveForm();
      await waitForElementToAppear(finalPageIndex, finalSectionIndex, finalElementIndex);
      openEditElement(finalPageIndex, finalSectionIndex, finalElementIndex);
      return;
    }

    if (sectionIndex >= page.sections.length) {
      console.error("Section index out of bounds");
      return;
    }

    const section = page.sections[sectionIndex];
    const elements = [...section.elements];

    if (elementIndex !== undefined && elementIndex >= 0 && elementIndex < elements.length) {
      elements.splice(elementIndex + 1, 0, newElement);
      finalElementIndex = elementIndex + 1;
    } else {
      elements.push(newElement);
      finalElementIndex = elements.length - 1;
    }

    setValue(`pages.${pageIndex}.sections.${sectionIndex}.elements`, elements);
    finalPageIndex = pageIndex;
    finalSectionIndex = sectionIndex;

    await handleSaveForm();
    await waitForElementToAppear(finalPageIndex, finalSectionIndex, finalElementIndex);
    openEditElement(finalPageIndex, finalSectionIndex, finalElementIndex);
  };

  const handleSaveForm = async () => {
    const formData = getValues() as ApiFormEdit;
    await formUpdate.mutateAsync({
      id: id,
      payload: {
        pages: formData.pages,
      },
    });
  };

  const openEditElement = (pageIndex: number, sectionIndex?: number, elementIndex?: number) => {
    const formData = getValues() as ApiFormEdit;
    let element: ApiFormElementEdit;

    if (sectionIndex !== undefined && elementIndex !== undefined) {
      element = formData.pages[pageIndex].sections[sectionIndex].elements[elementIndex];

      openModal(FormEditQuestionModal, {
        element: element,
        onSave: async (updatedElement) => {
          setValue(
            `pages.${pageIndex}.sections.${sectionIndex}.elements.${elementIndex}`,
            updatedElement
          );

          await handleSaveForm();
        },
        onDelete: async () => {
          const elementsPath = `pages.${pageIndex}.sections.${sectionIndex}.elements` as const;
          const elements = getValues(elementsPath);
          const updatedElements = [...elements];
          updatedElements.splice(elementIndex, 1);
          setValue(elementsPath, updatedElements);
          await handleSaveForm();
        },
      });
    } else if (sectionIndex !== undefined) {
      const sectionElement = formData.pages[pageIndex].sections[sectionIndex];
      openModal(FormEditTitleDescriptionModal, {
        data: {
          title: sectionElement.title,
          description: sectionElement.description,
        },
        onSave: async (title, description) => {
          setValue(`pages.${pageIndex}.sections.${sectionIndex}.title`, title);
          setValue(`pages.${pageIndex}.sections.${sectionIndex}.description`, description);

          await handleSaveForm();
        },
        onDelete: async () => {
          const sectionsPath = `pages.${pageIndex}.sections` as const;
          const sections = getValues(sectionsPath);
          const updatedSections = [...sections];
          updatedSections.splice(sectionIndex, 1);
          setValue(sectionsPath, updatedSections);
          await handleSaveForm();
        },
      });
    } else {
      const pageElement = formData.pages[pageIndex];
      openModal(FormEditTitleDescriptionModal, {
        data: {
          title: pageElement.title,
          description: pageElement.description,
        },
        onSave: async (title, description) => {
          setValue(`pages.${pageIndex}.title`, title);
          setValue(`pages.${pageIndex}.description`, description);

          await handleSaveForm();
        },
        onDelete: async () => {
          const pagesPath = `pages` as const;
          const pages = getValues(pagesPath);
          const updatedPages = [...pages];
          updatedPages.splice(pageIndex, 1);
          setValue(pagesPath, updatedPages);
          await handleSaveForm();
        },
      });
    }
  };

  const handleAddPage = async () => {
    const currentForm = getValues() as ApiFormEdit;
    const newPage: ApiFormPageEdit = {
      title: null,
      description: null,
      sections: [],
    };

    const updatedPages = [...currentForm.pages, newPage];
    setValue("pages", updatedPages);
    await handleSaveForm();
  };

  const getElementView = (elementId: number): ApiFormViewElement | null => {
    if (!form) return null;
    for (const page of form.pages) {
      for (const section of page.sections) {
        const element = section.elements.find((el) => el.id === elementId);
        if (element) {
          return element;
        }
      }
    }
    return null;
  };

  const handleStartFromScratch = async () => {
    if (formReset === undefined) return;
    openModal(ConfirmationModal, {
      question: t("surveys:start_from_scratch") + "?",
      text: t("surveys:start_from_scratch_confirm"),
      acceptTitle: t("surveys:start_over"),
      onAccept: async function () {
        try {
          await formReset.mutateAsync({ id: id });

          switch (type) {
            case FormEditorTypes.survey:
              analyticsService.trackEvent(analyticEvents.surveys.startedFromScratch, {
                [analyticProperties.id]: id,
              });
              break;
          }
        } catch {
          return;
        }
      },
    });
  };

  const handleSaveAsTemplate = async () => {
    if (!formSaveAsTemplate || !formEditorType.templateFolderModule) return;
    openModal(FormSaveTemplateModal, {
      module: formEditorType.templateFolderModule,
      onSave: async (payload) => {
        const template = await formSaveAsTemplate.mutateAsync({
          id: id,
          payload,
        });
        switch (type) {
          case FormEditorTypes.survey:
            analyticsService.trackEvent(analyticEvents.surveyTemplates.createdFromSurvey, {
              [analyticProperties.id]: template.id,
            });
            break;
          case FormEditorTypes.performanceReview:
            analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.createdFrom, {
              [analyticProperties.id]: template.id,
            });
            break;
        }
      },
    });
  };

  const handleDuplicateElement = async (
    pageIndex: number,
    sectionIndex: number,
    elementIndex: number
  ) => {
    const formData = getValues();

    const elements = formData.pages[pageIndex].sections[sectionIndex].elements;

    const elementOriginal = elements[elementIndex];

    const elementDuplicate =
      typeof structuredClone === "function"
        ? structuredClone(elementOriginal)
        : (JSON.parse(JSON.stringify(elementOriginal)) as ApiFormElementEdit);

    elementDuplicate.id = undefined;

    const elementDuplicateIndex = elementIndex + 1;
    const elementsUpdated = [...elements];
    elementsUpdated.splice(elementDuplicateIndex, 0, elementDuplicate);

    setValue(`pages.${pageIndex}.sections.${sectionIndex}.elements`, elementsUpdated);
    await handleSaveForm();

    openEditElement(pageIndex, sectionIndex, elementDuplicateIndex);
  };

  const triggerFormUpdate = () => {
    formQuery.refetch();
  };

  const value = {
    formId: id,
    formType: formEditorType.type,
    formEditorType: type,
    readOnly,
    formValues,
    selectedElement,
    setSelectedElement,
    toggleSelectedElement,
    handleAddPage,
    handleAddSection,
    handleAddElement,
    handleAddScale,
    handleAddGoalScale,
    handleSaveForm,
    openEditElement,
    methods,
    getElementView,
    handleSaveAsTemplate,
    handleStartFromScratch,
    handleDuplicateElement,
    triggerFormUpdate,
  };

  return <FormEditorContext.Provider value={value}>{children}</FormEditorContext.Provider>;
};
